/**
 * PSTUDIO-3848 — geometry for revealing a freshly inserted/pasted block of rows.
 *
 * Pure (React/DOM-free) so the branching + boundary math is unit-testable; the
 * `<Table/>` effect only reads the DOM coordinates and applies the returned
 * delta to `scrollTop`. All values are in the scroller's own viewport space
 * (px): `viewTop`/`viewBottom` are the usable band (top already offset PAST the
 * sticky header), `firstTop`/`lastBottom` are the block's outer edges.
 */
export interface RevealBlockMetrics {
  /** Top of the visible band = scroller top + sticky-header height. */
  viewTop: number;
  /** Bottom of the visible band = scroller top + clientHeight (excludes any
   *  horizontal scrollbar; NOT getBoundingClientRect().bottom). */
  viewBottom: number;
  /** Top edge of the first (top-most) row in the block. */
  firstTop: number;
  /** Bottom edge of the last (bottom-most) row in the block. */
  lastBottom: number;
}

/**
 * The delta to ADD to `scrollTop` so the block sits inside the visible band.
 * Positive scrolls the content up (reveals lower rows), negative scrolls down
 * (reveals upper rows), `0` when the block is already fully in the band.
 *
 * Priority when the block does NOT fit the band: show its FIRST row just under
 * the header, so the user always sees where the insert/paste begins.
 */
export function computeRevealScrollDelta(m: RevealBlockMetrics): number {
  const band = m.viewBottom - m.viewTop;
  const blockHeight = m.lastBottom - m.firstTop;
  // Taller than the band, or the top is above/under the header → anchor the
  // FIRST row to the top of the band. (When it fits, this branch only triggers
  // if the top is actually off — a fitting block whose top is in-band skips it.)
  if (blockHeight > band || m.firstTop < m.viewTop) {
    return m.firstTop - m.viewTop;
  }
  // Fits, top is fine, but the bottom is off → pull it up. Because it fits, the
  // first row then necessarily stays within the band.
  if (m.lastBottom > m.viewBottom) {
    return m.lastBottom - m.viewBottom;
  }
  // Already fully visible.
  return 0;
}
